// Replace your existing code with this full version

import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import Sidebar from './GroupSidebar';
import Select from 'react-select';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Container,
    Row,
    Col,
    Card,
    CardBody,
} from 'reactstrap';
import { postDataAPI } from "../../helpers/phpbackend_helper";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useSelector, useDispatch } from 'react-redux';
import Loader from "../../Components/Common/Loader";
import _ from "lodash"; 
import { userUpdateGroupsList } from '../../store/actions';

function ManageGroups() {
  const dispatch = useDispatch()
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newNodeType, setNewNodeType] = useState(null);
  const [newNodePos, setNewNodePos] = useState({ x: 0, y: 0 });
  const [inputValue, setInputValue] = useState('');
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [editNodeId, setEditNodeId] = useState(null);
  const [outletList, setOutletList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const { currentOutlet,userOutlets } = useSelector(state => ({
      currentOutlet: state.UserOutletGroup.currentOutlet,
      userOutlets: state.UserOutletGroup.userOutlets,
  }));

  useEffect(()=>{
      const existingOutlets = Object.values(userOutlets).map(outlet => ({
        id: outlet.outlet_id,
        label: outlet.outlet_name,
        currency: outlet.currency
      }));
      setOutletList(Object.values(existingOutlets));
  },[userOutlets]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    postDataAPI({
      serviceName: 'groups_data',
      op: 'list_groups',
    }).then((response) => {
      if (response.status === "ok") {
        const group_json = (response.data.group_data.length > 0) ? response.data.group_data[0]?.group_json : "";
        if (group_json) {
          const { nodes, edges } = JSON.parse(group_json);
          const updatedNodes = nodes.map((node) => ({
            ...node,
            data: {
              ...node.data,
              label: renderNodeLabel(node.id, node.data.labelText, node.id.split('-')[0]),
            },
          }));
          
          setHistory(prev => [...prev, { nodes, edges }]);
          setRedoStack([]);
          setNodes(updatedNodes);
          setEdges(edges);
        }
        setLoading(false);
      } else {
        console.error("Failed to get group:", response);
        setLoading(false);
      }
    });
    return () => {
        controller.abort();
    };
  }, []);

  const renderNodeLabel = (id, labelText, type) => (
    <div style={{ position: 'relative', paddingRight: 40 }}>
      {labelText}
      <span
        onClick={() => handleEditNode(id, labelText, type)}
        style={{ position: 'absolute', right: 20, top: 0, cursor: 'pointer', color: 'blue' }}
      >
        ✎
      </span>
      <span
        onClick={() => handleDeleteNode(id)}
        style={{ position: 'absolute', right: 0, top: 0, cursor: 'pointer', color: 'red' }}
      >
        X
      </span>
    </div>
  );

  const onConnect = useCallback(
    (connection) => {
      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);

      const valid = validateConnection(sourceNode, targetNode);
      if (!valid) {
        alert('Invalid hierarchy!');
        return;
      }

      const isAlreadyConnected = edges.some(
        (e) => e.source === connection.source && e.target === connection.target
      );
      if (isAlreadyConnected) {
        alert('Nodes already connected.');
        return;
      }

      const targetHasInput = edges.some((e) => e.target === connection.target);
      if (targetHasInput) {
        alert('This node already has a parent.');
        return;
      }

      setEdges((eds) =>
        addEdge({ ...connection, markerEnd: { type: MarkerType.Arrow } }, eds)
      );
    },
    [nodes, edges]
  );

  const validateConnection = (source, target) => {
    const sourceType = source.id.split('-')[0];
    const targetType = target.id.split('-')[0];

    const validHierarchy = {
      area: ['cluster', 'location', 'outlet'],
      cluster: ['location', 'outlet'],
      location: ['outlet'],
      outlet: [],
    };

    return validHierarchy[sourceType]?.includes(targetType);
  };

  const getColorForType = (type) => {
    switch (type) {
      case 'area': return '#d0ebff';
      case 'cluster': return '#d3f9d8';
      case 'location': return '#ffd8a8';
      case 'outlet': return '#ffe3e3';
      default: return '#f1f3f5';
    }
  };

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const bounds = event.target.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      setNewNodeType(type);
      setNewNodePos(position);
      setInputValue('');
      setSelectedOutlet(null);
      setEditNodeId(null);
      setModalIsOpen(true);
    },
    [reactFlowInstance]
  );

  const handleAddOrEditNode = () => {
    const labelText = newNodeType === 'outlet' ? selectedOutlet?.label : inputValue;
    if (!labelText) return;

    if (editNodeId) {
      // Edit existing node
      const updatedNodes = nodes.map((node) => {
        if (node.id === editNodeId) {
          return {
            ...node,
            data: {
              labelText,
              outlet_id: newNodeType === 'outlet' ? selectedOutlet?.id : undefined,
              label: renderNodeLabel(node.id, labelText, node.id.split('-')[0]),
            },
          };
        }
        return node;
      });
      setHistory(prev => [...prev, { nodes, edges }]);
      setRedoStack([]);
      setNodes(updatedNodes);
    } else {
      // Add new node
      const nodeId = `${newNodeType}-${+new Date()}`;
      const newNode = {
        id: nodeId,
        type: 'default',
        position: newNodePos,
        data: {
          labelText,
          outlet_id: newNodeType === 'outlet' ? selectedOutlet?.id : undefined,
          label: renderNodeLabel(nodeId, labelText, newNodeType),
        },
        style: { background: getColorForType(newNodeType), border: '1px solid #333' },
      };
      setHistory(prev => [...prev, { nodes, edges }]);
      setRedoStack([]);
      setNodes((nds) => [...nds, newNode]);
    }

    setModalIsOpen(false);
  };

  const handleEditNode = (id, labelText, type) => {
    setEditNodeId(id);
    setNewNodeType(type);
    if (type === 'outlet') {
      const outletOption = outletList.find((o) => o.label === labelText);
      setSelectedOutlet(outletOption || null);
      setInputValue('');
    } else {
      setInputValue(labelText);
      setSelectedOutlet(null);
    }
    setModalIsOpen(true);
  };

  const handleDeleteNode = (nodeId) => {
    const snapshotNodes = nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        label: renderNodeLabel(node.id, node.data.labelText, node.id.split('-')[0]),
      }
    }));
    const snapshotEdges = JSON.parse(JSON.stringify(edges));

    setHistory(prev => [...prev, { nodes: snapshotNodes, edges: snapshotEdges }]);
    setRedoStack([]);
    
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const validateCurrencyPerRootGroup = (flatNodes, outletList) => {
  // Map: outletId -> currency
  const outletCurrencyMap = outletList.reduce((acc, outlet) => {
    acc[outlet.id] = outlet.currency;
    return acc;
  }, {});

  // Map: nodeId -> node
  const nodeMap = flatNodes.reduce((acc, node) => {
    acc[node.id] = node;
    return acc;
  }, {});

  // Find root-level nodes (no parent_id)
  const rootNodes = flatNodes.filter(node => !node.parent_id);

  const errors = [];

  const collectAllOutletIds = (nodeId) => {
    const node = nodeMap[nodeId];
    let outletIds = node.outlet_ids || [];

    const children = flatNodes.filter(n => n.parent_id === nodeId);
    for (const child of children) {
      outletIds = outletIds.concat(collectAllOutletIds(child.id));
    }
    return outletIds;
  };

  for (const rootNode of rootNodes) {
    const allOutletIds = collectAllOutletIds(rootNode.id);
    const allCurrencies = allOutletIds
      .map(id => outletCurrencyMap[id])
      .filter(Boolean);
    const uniqueCurrencies = [...new Set(allCurrencies)];

    if (uniqueCurrencies.length > 1) {
      errors.push(
        `"${rootNode.title}" contains outlets with mixed currencies: ${uniqueCurrencies.join(', ')}`
      );
    }
  }

  return errors;
};


 const { userGroups} = useSelector(state => {
        return {
            userGroups: state.UserOutletGroup.userGroups,
        };
    });

  const saveTree = () => {
    const nodeHasParent = (nodeId) => edges.some((e) => e.target === nodeId);
    for (const node of nodes) {
      const nodeType = node.id.split('-')[0];
       if (nodeType === 'outlet' && !nodeHasParent(node.id)) {
        alert(`Outlet "${node.data.labelText}" must have a parent.`);
        return;
      }
    }
      
    const strippedNodes = nodes.map((node) => ({
      ...node,
      data: {
        labelText: node.data.labelText,
        outlet_id: node.data.outlet_id,
      },
    }));
  
    const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
    
    // 🔁 Build child → parent map
    const parentMap = {};
    edges.forEach(({ source, target }) => {
      parentMap[target] = source;
    });
  
    const collectOutletIds = (id) => {
      const node = nodeMap[id];
      const nodeType = node.id.split('-')[0];
      if (nodeType === 'outlet') return [node.data.outlet_id];
  
      const childrenEdges = edges.filter((e) => e.source === id);
      let outletIds = [];
      for (const edge of childrenEdges) {
        outletIds = outletIds.concat(collectOutletIds(edge.target));
      }
      return outletIds;
    };

    const outletCurrencyMap = outletList.reduce((acc, outlet) => {
      acc[outlet.id] = outlet.currency;
      return acc;
    }, {});
  
    const flatList = nodes.map((node) => {
      const type = node.id.split('-')[0];
      const title = node.data.labelText;
      const outletIds = type === 'outlet'
        ? [node.data.outlet_id]
        : collectOutletIds(node.id);
   
      let currency;
      const outletIds1 = collectOutletIds(node.id).filter(Boolean);
      const currencies = outletIds1.map(id => outletCurrencyMap[id]).filter(Boolean);
      const uniqueCurrencies = [...new Set(currencies)];
      currency = uniqueCurrencies.length === 1 ? uniqueCurrencies[0] : 'mixed';
  
      // 🔁 Get parent info
      const parentId = parentMap[node.id];
      const parentNode = parentId ? nodeMap[parentId] : null;
  
      return {
        id: node.id,
        type,
        title,
        outlet_ids: outletIds.filter(Boolean),
        parent_id: parentId || null,
        parent_title: parentNode?.data?.labelText || null,
        currency
      };
    });
    
    const errors = validateCurrencyPerRootGroup(flatList, outletList);
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return; 
    }
  
    //console.log(flatList); return false;
    setLoading(true);
    postDataAPI({
      serviceName: 'groups_data',
      op: 'save_groups',
      group_json: JSON.stringify({ nodes: strippedNodes, edges }),
      filtered_group_json: flatList,
      // app_user_id: appUserId, // Add actual user id here if needed
    }).then((response) => {
      setLoading(false);
      if (response.status === "ok") {
        console.log("Tree saved successfully");
        console.log(response.data);
        dispatch(userUpdateGroupsList(response.data.analytics_groups))
        // window.location.reload(true);
      } else {
        console.error("Failed to save tree:", response);
        // window.location.reload(true);
      }
    });
  };
  
  const buildTree = (nodes, edges) => {
    const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, { ...n, children: [] }]));
    edges.forEach(({ source, target }) => {
      nodeMap[source].children.push(nodeMap[target]);
    });
    return Object.values(nodeMap).filter(
      (node) => !edges.some((e) => e.target === node.id)
    );
  };

  const onInit = (instance) => {
    setReactFlowInstance(instance);
    instance.setViewport({zoom: 0.80 }); // 👈 Zoom out here
  };

  const onEdgeClick = (event, edge) => {
    event.stopPropagation(); // prevent triggering other click handlers
    setHistory(prev => [...prev, { nodes, edges }]);
    setRedoStack([]);
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
  };

  const handleUndo = () => {
    if (history.length > 1) {
      const newRedo = {
        nodes: nodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            label: renderNodeLabel(node.id, node.data.labelText, node.id.split('-')[0]),
          }
        })),
        edges: JSON.parse(JSON.stringify(edges))
      };

      setRedoStack(prev => [...prev, newRedo]);

      const previousState = history[history.length - 2];

      const restoredNodes = previousState.nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          label: renderNodeLabel(node.id, node.data.labelText, node.id.split('-')[0]),
        }
      }));

      setHistory(prev => prev.slice(0, prev.length - 1));
      setNodes(restoredNodes);
      setEdges(previousState.edges);
    }
  };


  const handleRedo = () => {
    if (redoStack.length === 0) return;

    const nextState = redoStack[redoStack.length - 1];
    setHistory(h => [...h, { nodes, edges }]);
    setRedoStack(r => r.slice(0, r.length - 1));
    setNodes(nextState.nodes);
    setEdges(nextState.edges);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'z') {
        handleUndo();
      } else if (e.ctrlKey && e.key === 'y') {
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [history, redoStack]);

  const handleNodesChange = (changes) => {
    onNodesChange(changes);

    const isDrag = changes.some(change => change.type === 'position');
    if (!isDrag) {
      pushToHistory(); // Only push if it's not a drag
    } 
  };

  const handleEdgesChange = (changes) => {
    onEdgesChange(changes);
    pushToHistory();
  };


  const pushToHistory = () => {
    const last = history[history.length - 1];
    const current = { nodes, edges };

    if (!_.isEqual(last, current)) {
      setHistory([...history, current]);
      setRedoStack([]);
    }
  };




  return (
    <div className="page-content">
      <Container fluid>
          <BreadCrumb title="Group Management" pageTitle="Admin" />
          <Row>
              <Col>
                  <Card>
                    <CardBody>
                    {loading && <Loader />}  
                    <div style={{ display: 'flex', height: '100vh' }} className={loading ? "opacity-0" : ""}>
                      <ReactFlowProvider>
                        <Sidebar />
                        <div style={{ flexGrow: 1 }} className="reactflow-wrapper">
                          <div className='text-end'>
                          <Button color="primary" onClick={saveTree}>Save Group</Button>
                          </div>
                          <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={handleNodesChange}
                            onEdgesChange={handleEdgesChange}
                            onConnect={onConnect}
                            onInit={onInit}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            onEdgeClick={onEdgeClick}
                            fitView
                          >
                            <MiniMap />
                            <Controls />
                            <Background />
                          </ReactFlow>

                          <Modal isOpen={modalIsOpen} toggle={() => setModalIsOpen(!modalIsOpen)}>
                            <ModalHeader toggle={() => setModalIsOpen(!modalIsOpen)}>
                              {editNodeId ? `Edit ${newNodeType}` : `Add ${newNodeType}`}
                            </ModalHeader>
                            <ModalBody>
                              {newNodeType === 'outlet' ? (
                                <div>
                                  <Label>Select Outlet</Label>
                                  <Select
                                    options={outletList}
                                    value={selectedOutlet}
                                    onChange={setSelectedOutlet}
                                    placeholder="Select Outlet"
                                  />
                                </div>
                              ) : (
                                <div>
                                  <Label>Enter {newNodeType} name</Label>
                                  <Input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={`Enter ${newNodeType} name`}
                                  />
                                </div>
                              )}
                            </ModalBody>
                            <ModalFooter>
                              <Button
                                color="primary"
                                onClick={handleAddOrEditNode}
                                disabled={
                                  (newNodeType === 'outlet' && !selectedOutlet) ||
                                  (newNodeType !== 'outlet' && !inputValue.trim())
                                }
                              >
                                {editNodeId ? 'Update' : 'Add'}
                              </Button>
                              <Button color="secondary" onClick={() => setModalIsOpen(false)}>Cancel</Button>
                            </ModalFooter>
                          </Modal>
                        </div>
                      </ReactFlowProvider>
                    </div>
                    </CardBody>
                  </Card>
              </Col>
            </Row>
      </Container>
    </div>

  );
}

export default ManageGroups;
